import { jwtDecode } from 'jwt-decode';

/**
 * Check if token is expired or about to expire
 * @param {string} token - JWT token to check
 * @param {number} bufferTime - Buffer time in seconds (default: 60)
 * @returns {boolean} - True if token is expired or will expire soon
 */
export const isTokenExpired = (token, bufferTime = 60) => {
    if (!token) return true;

    try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;

        // Check if token is expired or will expire within buffer time
        return decoded.exp < (currentTime + bufferTime);
    } catch (error) {
        console.error('Error decoding token:', error);
        return true;
    }
};

/**
 * Refresh the access token by calling the backend refresh endpoint
 * @returns {Promise<{success: boolean, token?: string, error?: string}>}
 */
export const refreshToken = async () => {
    try {
        const currentToken = localStorage.getItem('token');

        if (!currentToken) {
            return { success: false, error: 'No token available' };
        }

        // Check if current token is expired
        if (isTokenExpired(currentToken)) {
            // If token is expired, we might not be able to refresh
            // But still try in case refresh endpoint accepts expired tokens
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${currentToken}`
            },
            credentials: 'include', // Include cookies for refresh token if using httpOnly cookies
        });

        const result = await response.json();

        if (!response.ok) {
            return {
                success: false,
                error: result.error || result.message || `Refresh failed: ${response.status}`
            };
        }

        // Expected response format: { accessToken: "new_token", user?: {...} }
        const { accessToken, access_token, token: newToken } = result;
        const finalToken = accessToken || access_token || newToken;

        if (!finalToken) {
            return {
                success: false,
                error: 'No access token in refresh response'
            };
        }

        // Update localStorage with new token
        localStorage.setItem('token', finalToken);

        return {
            success: true,
            token: finalToken,
            user: result.user // Optional user data from response
        };

    } catch (error) {
        console.error('Token refresh error:', error);
        return {
            success: false,
            error: error.message || 'Network error during token refresh'
        };
    }
};

/**
 * Clear authentication data from localStorage
 */
export const clearAuthData = () => {
    localStorage.removeItem('token');
    // Clear any other auth-related data if needed
    // localStorage.removeItem('refreshToken');
    // localStorage.removeItem('user');
};

/**
 * Get current token from localStorage
 * @returns {string|null} - Current token or null
 */
export const getCurrentToken = () => {
    return localStorage.getItem('token');
};

/**
 * Check if user is currently authenticated
 * @returns {boolean} - True if authenticated with valid token
 */
export const isAuthenticated = () => {
    const token = getCurrentToken();
    return token && !isTokenExpired(token);
};